import {useEffect, useRef, useState, type MutableRefObject} from 'react';
import type {Location, NavigationType} from 'react-router-dom';

/**
 * Coalesce rapid pathname/search updates into one parent LOCATION_CHANGED postMessage.
 * In Jest, use 0 so parent sync is not delayed behind fake timers or macrotask ordering.
 */
const PARENT_LOCATION_SYNC_DEBOUNCE_MS = process.env.NODE_ENV === 'test' ? 0 : 50;

/**
 * Parent LOCATION_CHANGED debounce applies only while navigating within `/card-shield`.
 * Other routes sync immediately so non–Card Shield hosts are not affected by delayed postMessages.
 */
const isCardShieldLocationKey = (pathnameWithSearch: string): boolean => pathnameWithSearch.startsWith('/card-shield');

export type UseParentLocationSyncArgs = {
  location: Location;
  navigationType: NavigationType;
  updateParentLocation: (_location: Location) => void;
};

export type UseParentLocationSyncResult = {
  /** Set when applying parent-driven hash navigation so we can skip one echo of `LOCATION_CHANGED`. */
  lastParentDrivenHashRef: MutableRefObject<string | null>;
};

/**
 * Keeps the parent window URL/title in sync via debounced `LOCATION_CHANGED` postMessages.
 * Card Shield–only debounce; mirror-skip for parent-driven `navigate(..., { updateParent: false })`.
 */
export function useParentLocationSync({
  location,
  navigationType,
  updateParentLocation,
}: UseParentLocationSyncArgs): UseParentLocationSyncResult {
  const [prevLocation, setPrevLocation] = useState(location);
  const lastParentDrivenHashRef = useRef<string | null>(null);
  const parentLocationSyncTimerRef = useRef<number | null>(null);
  const pendingParentLocationRef = useRef<Location | null>(null);
  const updateParentLocationRef = useRef<(_location: Location) => void>(() => undefined);

  updateParentLocationRef.current = updateParentLocation;

  useEffect(() => {
    const pathnameChanged = prevLocation?.pathname !== location.pathname;
    const searchChanged = prevLocation?.search !== location.search;
    if (!pathnameChanged && !searchChanged) return;

    const currentHashWithSearch = `${location.pathname}${location.search || ''}`;
    const priorKey = `${prevLocation?.pathname || ''}${prevLocation?.search || ''}`;
    const leftCardShieldMerchantDetail =
      priorKey.startsWith('/card-shield/merchant/') && !currentHashWithSearch.startsWith('/card-shield/merchant/');
    const isParentDrivenMirror =
      !leftCardShieldMerchantDetail &&
      navigationType !== 'POP' &&
      location.state?.updateParent === false &&
      lastParentDrivenHashRef.current !== null &&
      lastParentDrivenHashRef.current === currentHashWithSearch;

    if (isParentDrivenMirror) {
      lastParentDrivenHashRef.current = null;
      if (parentLocationSyncTimerRef.current !== null) {
        clearTimeout(parentLocationSyncTimerRef.current);
        parentLocationSyncTimerRef.current = null;
      }
      pendingParentLocationRef.current = null;
    } else {
      if (parentLocationSyncTimerRef.current !== null) {
        clearTimeout(parentLocationSyncTimerRef.current);
      }
      pendingParentLocationRef.current = location;
      const cardShieldBurst = isCardShieldLocationKey(priorKey) && isCardShieldLocationKey(currentHashWithSearch);
      const debounceMs =
        navigationType === 'POP' || leftCardShieldMerchantDetail || !cardShieldBurst
          ? 0
          : PARENT_LOCATION_SYNC_DEBOUNCE_MS;

      const flushParentLocationSync = () => {
        parentLocationSyncTimerRef.current = null;
        const loc = pendingParentLocationRef.current;
        pendingParentLocationRef.current = null;
        if (loc) {
          updateParentLocationRef.current(loc);
        }
      };

      if (debounceMs === 0) {
        parentLocationSyncTimerRef.current = null;
        flushParentLocationSync();
      } else {
        parentLocationSyncTimerRef.current = window.setTimeout(flushParentLocationSync, debounceMs);
      }
    }
    setPrevLocation(location);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prevLocation is the previous-URL snapshot updated above; do not add to deps
  }, [location.pathname, location.search, navigationType]);

  useEffect(() => {
    return () => {
      if (parentLocationSyncTimerRef.current !== null) {
        clearTimeout(parentLocationSyncTimerRef.current);
        parentLocationSyncTimerRef.current = null;
      }
    };
  }, []);

  return {lastParentDrivenHashRef};
}
